import * as React from 'react';

interface Props
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  children: React.ReactNode;
  color?: 'primary' | 'secondary' | 'error' | 'success';
  variant?: 'text' | 'contained' | 'outlined';
  size?: 'md' | 'sm';
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent) => void;
}

// DESIGN.md §8.1 — variant (Primary/Secondary/Tertiary) × color (Brand/Neutral/Danger/+legacy success)
// matrix. The admin's existing API uses (variant: contained|outlined|text) × (color:
// primary|secondary|error|success); we keep that surface and map each pair to the
// closest styleguide cell.
const VARIANT_CLASS: Record<string, string> = {
  // contained → Primary tier
  'contained-primary':
    'bg-gradient-brand hover:bg-gradient-brand-hover text-text-inverse shadow-sm',
  'contained-secondary':
    'bg-surface-base border border-borderToken text-text-secondary hover:bg-surface-subtle',
  'contained-error':
    'bg-danger-500 text-text-inverse hover:opacity-90',
  // Legacy `success` color is preserved for visual continuity; foreground fixed
  // to white (the previous fg/bg same-color rendered the label invisible).
  'contained-success':
    'bg-success text-text-inverse hover:opacity-90',

  // outlined → Secondary tier
  'outlined-primary':
    'bg-surface-base border border-brand-500 text-text-brand hover:bg-surface-brand-soft',
  'outlined-secondary':
    'bg-surface-base border border-borderToken text-text-secondary hover:bg-surface-subtle',
  'outlined-error':
    'bg-surface-base border border-danger-500 text-danger-500 hover:bg-feedback-danger-bg',
  'outlined-success':
    'bg-surface-base border border-success text-success hover:bg-feedback-success-bg',

  // text → Tertiary tier
  'text-primary': 'text-text-brand hover:underline underline-offset-4',
  'text-secondary': 'text-text-secondary hover:underline underline-offset-4',
  'text-error': 'text-danger-500 hover:underline underline-offset-4',
  'text-success': 'text-feedback-success-fg hover:underline underline-offset-4',
};

const BASE_CLASS = [
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
  'transition-[background,color,box-shadow,transform] duration-150 ease-out',
  'active:translate-y-px',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
].join(' ');

// DESIGN.md §8.1.2 — size.md is default (pill); size.sm uses radius.md.
const SIZE_CLASS: Record<'md' | 'sm', string> = {
  md: 'h-10 min-h-[40px] px-4 text-base rounded-full',
  sm: 'h-8 min-h-[32px] px-3 text-sm rounded-md',
};

export default function Button({
  type = 'button',
  variant = 'contained',
  color = 'primary',
  size = 'md',
  children,
  disabled,
  className = '',
  onClick,
  ...props
}: Props) {
  const variantClass =
    VARIANT_CLASS[`${variant}-${color}`] ?? VARIANT_CLASS['contained-primary'];

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (disabled || !onClick) return;
      onClick(e);
    },
    [onClick, disabled],
  );

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={[BASE_CLASS, SIZE_CLASS[size], variantClass, className].join(' ')}
      {...props}
    >
      {children}
    </button>
  );
}
